import { differenceInDays } from 'date-fns';
import { sort } from 'fast-sort';
import {
  Estate_FieldsFragment,
  Estate_UpdateEstateGrantorInput,
  Estate_UpdateEstateInput,
  Estate_UpdateEstateTruadminFieldsInput,
  Estate_UpdateEstateTruplanningFieldsInput,
  Estate_UpdateEstateTruproxyFieldsInput,
  Estate_UpdateEstateTrustadminFieldsInput,
  Estate_UpdateEstateTrusteeInput,
  EstateGrantor_FieldsFragment,
  EstateResultItemType,
  EstateStatus,
  EstateTaskGroup,
  EstateTruadminFields_FieldsFragment,
  EstateTruplanningFields_FieldsFragment,
  EstateTruproxyFields_FieldsFragment,
  EstateTrustadminFields_FieldsFragment,
  EstateTrustee_FieldsFragment,
  EstateType,
  EstateWorkItemStatus,
  EstateWorkItemSubStatus,
} from 'queries';

type EstateStatusData = {
  [key in EstateStatus]: {
    label: string;
    adminLabel?: string;
    background?: string;
    border?: string;
    icon?: string;
  };
};

export const EstateStatusLabel: EstateStatusData = {
  [EstateStatus.DetailEntry]: {
    label: 'Entering Details',
  },
  [EstateStatus.DocumentUpload]: {
    label: 'Upload Documents',
    adminLabel: 'Uploading Documents',
  },
  [EstateStatus.Payment]: {
    label: 'Run Search',
    adminLabel: 'Payment Needed',
  },
  [EstateStatus.TrustateApproval]: {
    label: 'Pending Trustate Approval',
    border: '1px solid #F0F3F6',
  },
  [EstateStatus.ClientConsent]: {
    label: 'Pending Client Consent',
    background: '#F0F3F6',
    border: '1px solid #F0F3F6',
  },
  [EstateStatus.ClientConsentEmailSent]: {
    label: 'Client Consent Email Sent',
    background: '#F0F3F6',
    border: '1px solid #F0F3F6',
  },
  [EstateStatus.SearchInProcess]: {
    label: 'Results are Ready',
    background: '#7AE5A6',
    border: '1px solid #23B05C',
  },
  [EstateStatus.ResultsReady]: {
    label: 'Results are Ready',
    background: '#7AE5A6',
    border: '1px solid #23B05C',
  },
  [EstateStatus.ResultsExpired]: {
    label: 'Results are Expired',
    background: '#F0F3F6',
    border: '1px solid #F0F3F6',
  },
  [EstateStatus.Completed]: {
    label: 'Results are Ready',
    adminLabel: 'Completed',
    background: '#7AE5A6',
    border: '1px solid #23B05C',
  },
  [EstateStatus.Archived]: {
    label: 'Archived',
    background: '#F0F3F6',
    border: '1px solid #F0F3F6',
  },
};

export const EstateStatusExternalLabel: { [key in EstateStatus]: string } = {
  [EstateStatus.DetailEntry]: 'Not Started',
  [EstateStatus.DocumentUpload]: 'Not Started',
  [EstateStatus.Payment]: 'Not Started',
  [EstateStatus.TrustateApproval]: 'In Progress',
  [EstateStatus.ClientConsent]: 'In Progress',
  [EstateStatus.ClientConsentEmailSent]: 'In Progress',
  [EstateStatus.SearchInProcess]: 'In Progress',
  [EstateStatus.ResultsReady]: 'Completed',
  [EstateStatus.ResultsExpired]: 'Completed',
  [EstateStatus.Completed]: 'Completed',
  [EstateStatus.Archived]: 'Completed',
};

export function isEstateEditable(estateStatus: EstateStatus): boolean {
  return (
    estateStatus === EstateStatus.DetailEntry ||
    estateStatus === EstateStatus.DocumentUpload ||
    estateStatus === EstateStatus.Payment
  );
}

export function resultsAreReady(status: EstateStatus): boolean {
  return status === EstateStatus.ResultsReady || status === EstateStatus.Completed;
}

export function canClearResultsCache(
  ccrDate: string,
  consentDate: string,
): { canClear: boolean; reason: string } {
  let canClear = true;
  let reason = '';

  if (!ccrDate) {
    canClear = false;
    reason = 'not cached';
  } else if (consentDate && differenceInDays(new Date(), new Date(consentDate)) > 29) {
    // cannot clear the cache and fetch beyond the consent date
    canClear = false;
    reason = 'new consent required';
  }

  return { canClear, reason };
}

export function canPurgeResults(
  ccrDate: string,
  hasResults: boolean,
): { canPurge: boolean; reason: string } {
  let canPurge = true;
  let reason = '';

  if (!ccrDate) {
    canPurge = false;
    reason = 'not cached';
  }

  if (!hasResults) {
    canPurge = false;
    reason = 'no results';
  }

  return { canPurge, reason };
}

export function canPullResults(
  ccrDate: string,
  pvDate: string,
  consentDate: string,
  documentsVerified: boolean,
  hasResults: boolean,
): { canPull: boolean; reason: string } {
  let canPull = true;
  let reason = '';

  if (!consentDate) {
    canPull = false;
    reason = 'consent missing';
  } else if (!documentsVerified) {
    // cannot view without verified documents
    canPull = false;
    reason = 'verification missing';
  } else if (ccrDate && pvDate && hasResults) {
    // we already have cached data
    canPull = false;
    reason = 'already pulled';
  } else if (!ccrDate && consentDate && differenceInDays(new Date(), new Date(consentDate)) > 29) {
    canPull = false;
    reason = 'consent expired';
  }

  return { canPull, reason };
}

export function allDocumentsUploaded(
  estate: Estate_FieldsFragment,
  completedUploadEstateDocumentField?: string,
  deletedUploadEstateDocumentField?: string,
) {
  // check to see if both documents are uploaded, or if the other document was already uploaded
  const uploaded: { [id: string]: string } = {
    documentIdDeathCertificate: estate.documentIdDeathCertificate || '', // truadmin
    documentIdLettersTestamentary: estate.documentIdLettersTestamentary || '', // truadmin
    documentIdFrontOfPhotoId: estate.documentIdFrontOfPhotoId || '', // truplanning and truproxy
    documentIdBackOfPhotoId: estate.documentIdBackOfPhotoId || '', // truplanning and truproxy
    documentIdLegalDocumentation: estate.documentIdLegalDocumentation || '', // truproxy
  };

  // if an upload was just completed, included it
  if (completedUploadEstateDocumentField) {
    uploaded[completedUploadEstateDocumentField] = 'uploaded';
  }

  if (deletedUploadEstateDocumentField) {
    uploaded[deletedUploadEstateDocumentField] = '';
  }

  if (estate.type === EstateType.Truadmin || estate.type === EstateType.Trustadmin) {
    return uploaded.documentIdDeathCertificate && uploaded.documentIdLettersTestamentary
      ? true
      : false;
  }

  if (estate.type === EstateType.Truplanning) {
    return uploaded.documentIdFrontOfPhotoId && uploaded.documentIdBackOfPhotoId ? true : false;
  }

  if (estate.type === EstateType.Truproxy) {
    return uploaded.documentIdFrontOfPhotoId &&
      uploaded.documentIdBackOfPhotoId &&
      uploaded.documentIdLegalDocumentation
      ? true
      : false;
  }

  return false;
}

export function activeStatuses(): EstateStatus[] {
  return [
    EstateStatus.DetailEntry,
    EstateStatus.DocumentUpload,
    EstateStatus.TrustateApproval,
    EstateStatus.Payment,
    EstateStatus.ClientConsent,
    EstateStatus.ClientConsentEmailSent,
    EstateStatus.ResultsReady,
  ];
}

export function getEstateTaskGroupSelectOptions(estateType: EstateType) {
  const taskGroupMapping = {
    [EstateType.Truadmin]: [
      EstateTaskGroup.TruadminFirmSpecificTasks,
      EstateTaskGroup.TruadminEstateOpening,
      EstateTaskGroup.TruadminAssetLiabilityDiscovery,
      EstateTaskGroup.TruadminAssetLiabilityManagement,
      EstateTaskGroup.TruadminPaymentsTaxes,
      EstateTaskGroup.TruadminDistributions,
      EstateTaskGroup.TruadminEstateClosure,
    ],
    [EstateType.Truplanning]: [
      EstateTaskGroup.TruplanningFirmSpecificTasks,
      EstateTaskGroup.TruplanningEstatePlanning,
      EstateTaskGroup.TruplanningTrustFunding,
      EstateTaskGroup.TruplanningPostEstatePlanning,
      EstateTaskGroup.TruplanningAssetLiabilityDiscovery,
      EstateTaskGroup.TruplanningOpTipsForIrrevocableTrusts,
      EstateTaskGroup.TruplanningBusinessInterests,
    ],
    [EstateType.Truproxy]: [
      EstateTaskGroup.TruproxyFirmSpecificTasks,
      EstateTaskGroup.TruproxyGuardianshipsConservatorships,
    ],
    [EstateType.Trustadmin]: [
      EstateTaskGroup.TrustadminFirmSpecificTasks,
      EstateTaskGroup.TrustadminGrantorAssetAndLiabilityDiscovery,
      EstateTaskGroup.TrustadminTrusteeDiligenceTrusteeAcceptance,
      EstateTaskGroup.TrustadminTrustAccountingTrustReporting,
      EstateTaskGroup.TrustadminPostDeathAdministration,
    ],
  };

  return (
    taskGroupMapping[estateType].map((group) => ({
      value: group,
      label: EstateTaskGroupLabel[group],
    })) || []
  );
}

export const EstateResultItemInfo: { [key in EstateResultItemType]: string } = {
  [EstateResultItemType.Source]: '',
  [EstateResultItemType.Name]: 'Have Trustate search for unclaimed property under this name.',
  [EstateResultItemType.Employment]:
    "Have Trustate notify this employer of the death, inquire as to any unpaid compensation, unpaid employee benefits, or survivor's benefits.",
  [EstateResultItemType.Address]: 'Have Trustate locate the deed to this property for you.',
  [EstateResultItemType.Property]: 'Have Trustate locate the deed to this property for you.',
  [EstateResultItemType.Bankruptcy]: '',
  [EstateResultItemType.Collection]:
    'Have Trustate notify this institution of the death, get a final balance, and request any late fees be waived.',
  [EstateResultItemType.Document]: '',
  [EstateResultItemType.FormW2]: '',
  [EstateResultItemType.Form_1040]: '',
  [EstateResultItemType.Form_1098]: '',
  [EstateResultItemType.Form_1099]: '',
  [EstateResultItemType.Form_5498]: '',
  [EstateResultItemType.Trade]:
    'Have Trustate notify this institution of the death, get a final balance, and request any late fees be waived.',
  [EstateResultItemType.Account]: 'Have Trustate confirm whether or not they have an account here.',
};

// ! Changing these values will disrupt filtering on the MasterTaskList data grid
// ! Please update the corresponding value in api/src/enums/estate-task/estate-task-group.enum.ts
export const EstateTaskGroupLabel: { [key in EstateTaskGroup]: string } = {
  // TRUADMIN
  [EstateTaskGroup.TruadminFirmSpecificTasks]: 'Firm-Specific Tasks',
  [EstateTaskGroup.TruadminEstateOpening]: 'Phase 1: Estate Opening',
  [EstateTaskGroup.TruadminAssetLiabilityDiscovery]: 'Phase 2: Asset & Liability Discovery',
  [EstateTaskGroup.TruadminAssetLiabilityManagement]: 'Phase 3: Asset & Liability Management',
  [EstateTaskGroup.TruadminPaymentsTaxes]: 'Phase 4: Payments & Taxes',
  [EstateTaskGroup.TruadminDistributions]: 'Phase 4: Distributions',
  [EstateTaskGroup.TruadminEstateClosure]:
    'Phase 6: Estate Closure, Settlement, & Final Distributions',

  // TRUPLANNING
  [EstateTaskGroup.TruplanningFirmSpecificTasks]: 'Firm-Specific Tasks',
  [EstateTaskGroup.TruplanningEstatePlanning]: 'Estate Planning Document Creation and Signing',
  [EstateTaskGroup.TruplanningTrustFunding]: 'Trust Funding',
  [EstateTaskGroup.TruplanningPostEstatePlanning]: 'Post Estate Planning Updates',
  [EstateTaskGroup.TruplanningAssetLiabilityDiscovery]: 'Asset & Liability Discovery',
  [EstateTaskGroup.TruplanningOpTipsForIrrevocableTrusts]:
    'Operational Tips for Irrevocable Trusts',
  [EstateTaskGroup.TruplanningBusinessInterests]: 'For Business Interests',

  // TRUPROXY
  [EstateTaskGroup.TruproxyFirmSpecificTasks]: 'Firm-Specific Tasks',
  [EstateTaskGroup.TruproxyGuardianshipsConservatorships]: 'Searches',

  // TRUSTAADMIN
  [EstateTaskGroup.TrustadminFirmSpecificTasks]: 'Firm-Specific Tasks',
  [EstateTaskGroup.TrustadminGrantorAssetAndLiabilityDiscovery]:
    'Grantor Asset & Liability Discovery',
  [EstateTaskGroup.TrustadminTrusteeDiligenceTrusteeAcceptance]: 'Trustee Diligence & Acceptance',
  [EstateTaskGroup.TrustadminTrustAccountingTrustReporting]: 'Trust Accounting & Reporting',
  [EstateTaskGroup.TrustadminPostDeathAdministration]: 'Post-Death Administration',

  // DELETED
  [EstateTaskGroup.Deleted]: 'Deleted',
};

export const EstateWorkItemStatusLabel: { [key in EstateWorkItemStatus]: string } = {
  [EstateWorkItemStatus.AssignedReady]: 'Assigned: Ready',
  [EstateWorkItemStatus.Completed]: 'Completed',
};

export const EstateWorkItemSubStatusLabel: { [key in EstateWorkItemSubStatus]: string } = {
  [EstateWorkItemSubStatus.NotStarted]: 'Not Started',
  [EstateWorkItemSubStatus.InProgress]: 'In Progress',
  [EstateWorkItemSubStatus.InProgressBankInquiry]: 'In Progress: Bank Inquiry',
  [EstateWorkItemSubStatus.InProgressInsuranceInquiry]: 'In Progress: Insurance Inquiry',
  [EstateWorkItemSubStatus.InProgressPrinting]: 'In Progress: Printing',
  [EstateWorkItemSubStatus.InProgressWaitingOnClientInfo]:
    'In Progress: Waiting on Requested Client Info',
  [EstateWorkItemSubStatus.InProgressWaitingOnClientSignature]:
    'In Progress: Waiting on Client (Signature)',
  [EstateWorkItemSubStatus.InProgressWaitingOnInstitution]: 'In Progress: Waiting on Institution',
  [EstateWorkItemSubStatus.UnderReview]: 'Under Review',
  [EstateWorkItemSubStatus.UserRequestFulfilled]: 'User Request Fulfilled',
  [EstateWorkItemSubStatus.PreparingPaperwork]: 'Preparing Paperwork',
  [EstateWorkItemSubStatus.Stale]: 'Stale',
  [EstateWorkItemSubStatus.Completed]: 'Completed',
  [EstateWorkItemSubStatus.Closed]: 'Closed',
  [EstateWorkItemSubStatus.AutoGenerationInProgress]: 'Auto Gen (In Progress)',
  [EstateWorkItemSubStatus.AutoGenerationTemplateNeeded]: 'Auto Gen (Template Needed)',
  [EstateWorkItemSubStatus.AutoGenerationAwaitingSignature]: 'Auto Gen (Awaiting Signature)',
  [EstateWorkItemSubStatus.AutoGenerationReadyToPrint]: 'Auto Gen (Ready to Print)',
  [EstateWorkItemSubStatus.AutoGenerationCompleted]: 'Auto Gen (Completed)',
};

export const EstateWorkItemSubStatusExternalLabel: { [key in EstateWorkItemSubStatus]: string } = {
  [EstateWorkItemSubStatus.NotStarted]: 'Under Review',
  [EstateWorkItemSubStatus.InProgress]: 'In Progress',
  [EstateWorkItemSubStatus.InProgressPrinting]: 'In Progress',
  [EstateWorkItemSubStatus.InProgressBankInquiry]: 'Waiting On Institution',
  [EstateWorkItemSubStatus.InProgressInsuranceInquiry]: 'Waiting On Institution',
  [EstateWorkItemSubStatus.InProgressWaitingOnClientInfo]: 'Action Needed',
  [EstateWorkItemSubStatus.InProgressWaitingOnClientSignature]: 'Signature Needed',
  [EstateWorkItemSubStatus.InProgressWaitingOnInstitution]: 'Waiting On Institution',
  [EstateWorkItemSubStatus.UnderReview]: 'Under Review',
  [EstateWorkItemSubStatus.UserRequestFulfilled]: 'Under Review',
  [EstateWorkItemSubStatus.PreparingPaperwork]: 'In Progress',
  [EstateWorkItemSubStatus.Stale]: 'Action Needed',
  [EstateWorkItemSubStatus.Completed]: 'Completed',
  [EstateWorkItemSubStatus.Closed]: 'Closed',
  [EstateWorkItemSubStatus.AutoGenerationInProgress]: 'Click to Generate',
  [EstateWorkItemSubStatus.AutoGenerationTemplateNeeded]: 'Underway - Available in 24h',
  [EstateWorkItemSubStatus.AutoGenerationAwaitingSignature]: 'Signature Needed',
  [EstateWorkItemSubStatus.AutoGenerationReadyToPrint]: 'Signed',
  [EstateWorkItemSubStatus.AutoGenerationCompleted]: 'Click to Review',
};

const getEstateUpdateEstateTrusteeInputData = (
  estateTrusteeFields: EstateTrustee_FieldsFragment[],
): Estate_UpdateEstateTrusteeInput[] => {
  const trustees = estateTrusteeFields.map((trustee) => ({
    id: trustee.id,
    trusteeFirstName: trustee.trusteeFirstName,
    trusteeLastName: trustee.trusteeLastName,
    trusteeMiddleName: trustee.trusteeMiddleName,
    trusteeEmail: trustee.trusteeEmail,
    isPrimaryTrustee: trustee.isPrimaryTrustee,
    trusteePhoneNumber: trustee.trusteePhoneNumber,
    trusteeAddressCity: trustee.trusteeAddressCity,
    trusteeAddressState: trustee.trusteeAddressState,
    trusteeAddressStreet: trustee.trusteeAddressStreet,
    trusteeAddressZip: trustee.trusteeAddressZip,
    sortOrder: trustee.sortOrder,
  }));

  return sort(trustees).asc(({ sortOrder }) => sortOrder);
};

const getEstateUpdateEstateGrantorInputData = (
  estateGrantorFields: EstateGrantor_FieldsFragment[],
): Estate_UpdateEstateGrantorInput[] => {
  return estateGrantorFields?.map((grantor) => ({
    id: grantor.id,
    grantorFirstName: grantor.grantorFirstName,
    grantorLastName: grantor.grantorLastName,
    grantorMiddleName: grantor.grantorMiddleName,
    grantorSsn: grantor.grantorSsn,
    isGrantorStillAlive: grantor.isGrantorStillAlive,
    isPrimaryGrantor: grantor.isPrimaryGrantor,
    grantorAddressStreet: grantor.grantorAddressStreet,
    grantorAddressCity: grantor.grantorAddressCity,
    grantorAddressState: grantor.grantorAddressState,
    grantorAddressZip: grantor.grantorAddressZip,
    sortOrder: grantor.sortOrder,
  }));
};

const getEstateUpdateEstateTrustadminFieldsInputData = (
  estateTrustadminFields: EstateTrustadminFields_FieldsFragment,
): Estate_UpdateEstateTrustadminFieldsInput => {
  return {
    id: estateTrustadminFields!.id,
    trustName: estateTrustadminFields!.trustName,
    trustDate: estateTrustadminFields?.trustDate,
    isGrantorTrust: estateTrustadminFields?.isGrantorTrust,
    isRevocableTrust: estateTrustadminFields?.isRevocableTrust,
    ein: estateTrustadminFields?.ein,
    estateGrantors: estateTrustadminFields?.estateGrantors
      ? getEstateUpdateEstateGrantorInputData(estateTrustadminFields?.estateGrantors)
      : undefined,
    estateTrustees: estateTrustadminFields?.estateTrustees
      ? getEstateUpdateEstateTrusteeInputData(estateTrustadminFields?.estateTrustees)
      : undefined,
  };
};

const getEstateUpdateEstateTruadminFieldsInputData = (
  estateTruadminFields: EstateTruadminFields_FieldsFragment,
): Estate_UpdateEstateTruadminFieldsInput => {
  return {
    id: estateTruadminFields!.id,
    decedentFirstName: estateTruadminFields!.decedentFirstName,
    decedentLastName: estateTruadminFields?.decedentLastName,
    decedentMiddleName: estateTruadminFields?.decedentMiddleName,
    decedentDateOfBirth: estateTruadminFields?.decedentDateOfBirth,
    decedentDateOfDeath: estateTruadminFields?.decedentDateOfDeath,
    decedentSsn: estateTruadminFields?.decedentSsn,
    decedentLastAddressStreet: estateTruadminFields?.decedentLastAddressStreet,
    decedentLastAddressCity: estateTruadminFields?.decedentLastAddressCity,
    decedentLastAddressState: estateTruadminFields?.decedentLastAddressState,
    decedentLastAddressZip: estateTruadminFields?.decedentLastAddressZip,
    clientFirstName: estateTruadminFields?.clientFirstName,
    clientLastName: estateTruadminFields?.clientLastName,
    clientMiddleName: estateTruadminFields?.clientMiddleName,
    clientEmailAddress: estateTruadminFields?.clientEmailAddress,
    clientPhoneNumber: estateTruadminFields?.clientPhoneNumber,
    clientAddressStreet: estateTruadminFields?.clientAddressStreet,
    clientAddressCity: estateTruadminFields?.clientAddressCity,
    clientAddressState: estateTruadminFields?.clientAddressState,
    clientAddressZip: estateTruadminFields?.clientAddressZip,
    clientFiduciaryRole: estateTruadminFields?.clientFiduciaryRole,
    corporatePersonalRepresentativeName: estateTruadminFields?.corporatePersonalRepresentativeName,
    isTheClientTheSurvivingSpouse: estateTruadminFields?.isTheClientTheSurvivingSpouse,
  };
};

const getEstateUpdateEstateTruproxyFieldsInputData = (
  estateTruproxyFields: EstateTruproxyFields_FieldsFragment,
): Estate_UpdateEstateTruproxyFieldsInput => {
  return {
    id: estateTruproxyFields!.id,
    wardFirstName: estateTruproxyFields!.wardFirstName,
    wardLastName: estateTruproxyFields?.wardLastName,
    wardMiddleName: estateTruproxyFields?.wardMiddleName,
    wardSsn: estateTruproxyFields?.wardSsn,
    wardDateOfBirth: estateTruproxyFields?.wardDateOfBirth,
    wardAddressStreet: estateTruproxyFields?.wardAddressStreet,
    wardAddressCity: estateTruproxyFields?.wardAddressCity,
    wardAddressState: estateTruproxyFields?.wardAddressState,
    wardAddressZip: estateTruproxyFields?.wardAddressZip,
    clientFirstName: estateTruproxyFields?.clientFirstName,
    clientLastName: estateTruproxyFields?.clientLastName,
    clientEmailAddress: estateTruproxyFields?.clientEmailAddress,
    clientPhoneNumber: estateTruproxyFields?.clientPhoneNumber,
    clientAddressStreet: estateTruproxyFields?.clientAddressStreet,
    clientAddressCity: estateTruproxyFields?.clientAddressCity,
    clientAddressState: estateTruproxyFields?.clientAddressState,
    clientAddressZip: estateTruproxyFields?.clientAddressZip,
    clientRoleWithinEstate: estateTruproxyFields?.clientRoleWithinEstate,
  };
};

const getEstateUpdateEstateTruplanningFieldsInputData = (
  estateTruplanningFields: EstateTruplanningFields_FieldsFragment,
): Estate_UpdateEstateTruplanningFieldsInput => {
  return {
    id: estateTruplanningFields!.id,
    clientFirstName: estateTruplanningFields?.clientFirstName,
    clientMiddleName: estateTruplanningFields?.clientMiddleName,
    clientLastName: estateTruplanningFields?.clientLastName,
    clientEmailAddress: estateTruplanningFields?.clientEmailAddress,
    clientPhoneNumber: estateTruplanningFields?.clientPhoneNumber,
    clientSsn: estateTruplanningFields?.clientSsn,
    clientDateOfBirth: estateTruplanningFields?.clientDateOfBirth,
    clientAddressStreet: estateTruplanningFields?.clientAddressStreet,
    clientAddressCity: estateTruplanningFields?.clientAddressCity,
    clientAddressState: estateTruplanningFields?.clientAddressState,
    clientAddressZip: estateTruplanningFields?.clientAddressZip,
    isClientMarried: estateTruplanningFields?.isClientMarried,
    areClientsGrantors: estateTruplanningFields?.areClientsGrantors,
    spouseFirstName: estateTruplanningFields?.spouseFirstName,
    spouseLastName: estateTruplanningFields?.spouseLastName,
    spouseMiddleName: estateTruplanningFields?.spouseMiddleName,
    spouseEmailAddress: estateTruplanningFields?.spouseEmailAddress,
    spousePhoneNumber: estateTruplanningFields?.spousePhoneNumber,
    spouseSsn: estateTruplanningFields?.spouseSsn,
    spouseDOB: estateTruplanningFields?.spouseDOB,
    spouseAddressStreet: estateTruplanningFields?.spouseAddressStreet,
    spouseAddressCity: estateTruplanningFields?.spouseAddressCity,
    spouseAddressState: estateTruplanningFields?.spouseAddressState,
    spouseAddressZip: estateTruplanningFields?.spouseAddressZip,
  };
};

export const getEstateUpdateEstateInputData = (
  estate: Estate_FieldsFragment,
): Estate_UpdateEstateInput => {
  return {
    id: estate.id,
    portalId: estate.portalId,
    estateNumber: estate.estateNumber,
    documentIdLettersTestamentary: estate.documentIdLettersTestamentary,
    documentIdDeathCertificate: estate.documentIdDeathCertificate,
    documentIdFrontOfPhotoId: estate.documentIdFrontOfPhotoId,
    documentIdBackOfPhotoId: estate.documentIdBackOfPhotoId,
    documentIdLegalDocumentation: estate.documentIdLegalDocumentation,
    clientConsentReceived: estate.clientConsentReceived,
    status: estate.status,
    assignedUserId: estate.assignedUserId,
    estateTrustadminFields: estate.estateTrustadminFields
      ? getEstateUpdateEstateTrustadminFieldsInputData(estate.estateTrustadminFields)
      : undefined,
    estateTruproxyFields: estate.estateTruproxyFields
      ? getEstateUpdateEstateTruproxyFieldsInputData(estate.estateTruproxyFields)
      : undefined,
    estateTruadminFields: estate.estateTruadminFields
      ? getEstateUpdateEstateTruadminFieldsInputData(estate.estateTruadminFields)
      : undefined,
    estateTruplanningFields: estate.estateTruplanningFields
      ? getEstateUpdateEstateTruplanningFieldsInputData(estate.estateTruplanningFields)
      : undefined,
  };
};
